import { Building2, ChevronDown, Globe2, Info, Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const AccessIcons = {
  User: Lock,
  Workspace: Building2,
  Global: Globe2,
};

const AccessInfo = {
  User: "Only specific people can access",
  Workspace: "Anyone in your workspace can access",
  Global: "Anyone can access",
};

const AccessButtonText = {
  User: "Restricted",
  Workspace: "Workspace",
  Global: "Global",
};

interface ShareAccessProps {
  visibilityScope: "User" | "Workspace" | "Global";
  onChange?: (scope: "User" | "Workspace") => void;
  canShareToWorkspace?: boolean;
  infoMessage?: string | React.ReactNode;
  className?: string;
  title?: string;
}

export function ShareAccess({
  visibilityScope,
  onChange,
  canShareToWorkspace = false,
  infoMessage,
  className,
  title = "General Access",
}: ShareAccessProps) {
  const Icon = AccessIcons[visibilityScope];

  return (
    <div className={cn("space-y-3", className)}>
      {title && <div className="font-semibold">{title}</div>}
      <div className="flex items-center">
        <div className="w-9 h-9 rounded-full bg-blue-200 flex items-center justify-center mr-3">
          <Icon className="w-4 h-4 text-blue-900" />
        </div>
        <div className="flex-1">
          {canShareToWorkspace && onChange && visibilityScope !== "Global" ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button intent="secondary" size="sm">
                  {AccessButtonText[visibilityScope]}
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem onClick={() => onChange("User")}>
                  {AccessButtonText.User}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onChange("Workspace")}>
                  {AccessButtonText.Workspace}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="text-sm font-medium leading-none">
              {AccessButtonText[visibilityScope]}
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1">
            {AccessInfo[visibilityScope]}
          </div>
        </div>
      </div>

      {infoMessage && (
        <div className="flex mt-5 items-start gap-3 p-3 bg-gray-100 border border-border rounded-md">
          <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs m-0 text-gray-500 leading-relaxed">
            {infoMessage}
          </p>
        </div>
      )}
    </div>
  );
}
